import { FormItemType } from '../../UI/view/component-map';
import { ListView, ListViewColumnType, initListView } from '../../UI/view/list-view';
import { DataSource } from '../../UI/view/data-source';
import {
  getOrderDewarShipments,
  addOrderDewarShipment,
  updateOrderDewarShipment,
  deleteOrderDewarShipment,
  getOrderDeliveryTypes,
  repriceOrderShipments,
} from '../../../services/requests/orders/orders-requests';
import { GetDewarProductTypeList } from '../../../services/requests/billing/dewar-billing-requests';
import { OrderType } from './orders-types';
import { ordersActionPermissions } from './orders-datasource';

// Currency formatter mirroring legacy {0:C} (2 decimals) / {0:C3} (3 decimals),
// with thousands separators.
const currency = (v: any, decimals = 2) => {
  const n = Number(v);
  return Number.isFinite(n)
    ? `$${n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`
    : '';
};

// Legacy Products Delivered "Total" = Qty x UnitPrice, zeroed once the line has a
// return date (Orders.OrderDetails.Total).
const shipmentLineTotal = (row: any) =>
  (Number(row?.qtyDelivered) || 0) * (Number(row?.unitPrice) || 0) * (row?.returnDate ? 0 : 1);



// Products Delivered grid (legacy divProductDetails / dbo.OrderDetails), Dewar-
// only, as a reusable CRUD listview. Add/edit/delete persist immediately via the
// dedicated shipment endpoints; the server recomputes fees + product unit price
// (ComputeProductUnitPriceAsync) so no client pricing is needed. Scoped to the
// loaded order via requestArgs; new rows get orderId via addTemplate.
export const orderDewarShipmentsDataSource = {
  name: 'orderDewarShipments',
  pk: 'id',
  request: getOrderDewarShipments,
  requestArgs: (state: any) => state.orders?.current?.data?.id,
  createRequest: addOrderDewarShipment,
  updateRequest: updateOrderDewarShipment,
  deleteRequest: deleteOrderDewarShipment,
} as DataSource;

const dewarProductOptions = async () => {
  const list = await GetDewarProductTypeList();
  return [
    { id: '', text: '-- Select One --' },
    ...(list ?? [])
      .filter((p: any) => p.isActive !== false)
      .map((p: any) => ({
        id: p.id,
        text: p.description ? `${p.productNumber} - ${p.description}` : p.productNumber,
      })),
  ];
};

// Per-line delivery-type override (legacy ddlDewarShipmentDeliveryType). Products
// Delivered is Dewar-only, so the list is always the Dewar delivery types. The
// stored value is the delivery type NAME (OrderDetails.Freight), matching legacy.
const deliveryTypeNameOptions = async () => {
  const list = await getOrderDeliveryTypes(OrderType.Dewar);
  return [
    { id: '', text: '-- Select One --' },
    ...(list ?? []).map((d: any) => ({ id: d.name ?? d.Name, text: d.name ?? d.Name })),
  ];
};

// Serial-number entry (legacy tbEditDewarShipmentSerialNumber). Legacy uses a
// plain text box: a typed serial always commits, and the server resolves it to
// the container FK id on save (a react-bootstrap typeahead with allowNew silently
// discarded free-typed serials unless the "Add new" popup was clicked).

// Shared add/edit modal form. The parent orderId is seeded onto new rows via
// addTemplate and flows through handleSubmit (undeclared fields are preserved);
// on edit the row (currentEntity) already carries id + orderId.
export const orderDewarShipmentForm = {
  permissions: ordersActionPermissions,
  addTemplate: (state: any) => ({ orderId: state.orders?.current?.data?.id, qtyDelivered: 1 }),
  items: [
    { name: 'productTypeId', label: 'Product', type: FormItemType.select, searchable: true, required: true, options: dewarProductOptions },
    { name: 'freight', label: 'Delivery Type', type: FormItemType.select, searchable: true, options: deliveryTypeNameOptions },
    { name: 'qtyDelivered', label: 'Qty (Liters)', type: FormItemType.text, number: true, defaultValue: 1 },
    { name: 'deliveryDate', label: 'Date/Time Delivered', type: FormItemType.datetime },
    { name: 'returnDate', label: 'Date/Time Returned', type: FormItemType.datetime },
    { name: 'stopRentDate', label: 'Early Rent Stop Date', type: FormItemType.date },
    { name: 'serialNumber', label: 'Serial Number', type: FormItemType.text },
    { name: 'unitPrice', label: 'Unit Price', type: FormItemType.text, number: true },
    { name: 'description', label: 'Description', type: FormItemType.text },
  ],
};

export const orderDewarShipmentsListView = {
  permissions: ordersActionPermissions,
  create: orderDewarShipmentForm,
  update: orderDewarShipmentForm,
  hideGlobalFilter: true,
  addButtonText: 'Add Item',
  // Legacy hides the Products Delivered add controls until Date Fulfilled is
  // populated, and continues hiding Add Item after the order is Closed.
  hideAddButton: ({ watch }: any) => !watch?.('dateFulfilled') || watch?.('statusText') === 'Closed',
  toolbar: [
    {
      type: FormItemType.button,
      label: 'Auto Reprice Order Items',
      permissions: ordersActionPermissions,
      hideIf: ({ watch }: any) => !watch?.('dateFulfilled'),
      handler: async ({ getValues, loadData }: any) => {
        const orderId = getValues?.('id') ?? getValues?.()?.id;
        if (!orderId) return;
        await repriceOrderShipments(orderId);
        loadData?.();
      },
    },
  ],
  columns: [
    { type: ListViewColumnType.edit, accessor: 'edit' },
    { type: ListViewColumnType.delete, accessor: 'delete' },
    { Header: 'Item', accessor: 'productName' },
    { Header: 'Serial #', accessor: 'serialNumber' },
    { Header: 'Qty', accessor: 'qtyDelivered' },
    { Header: 'Delivery Date', accessor: 'deliveryDate', type: ListViewColumnType.date },
    { Header: 'Return Date', accessor: 'returnDate', type: ListViewColumnType.date },
    { Header: 'Unit Price', id: 'unitPrice', accessor: (row: any) => currency(row?.unitPrice, 3) },
    {
      Header: 'Total',
      id: 'total',
      accessor: (row: any) => currency(shipmentLineTotal(row)),
      Footer: (info: any) => `Total: ${currency((info?.rows ?? []).reduce((sum: number, r: any) => sum + shipmentLineTotal(r.original), 0))}`,
    },
    { Header: 'Stop Rent Date', accessor: 'stopRentDate', type: ListViewColumnType.date },
    { Header: 'Description', accessor: 'description' },
  ],
} as ListView;

export const OrderDewarShipmentsListView = initListView(orderDewarShipmentsListView, orderDewarShipmentsDataSource);
